import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16 }
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }
const stat = { padding: '16px', background: '#0f172a', borderRadius: 8, marginBottom: 12 }
const statLabel = { fontSize: 12, color: '#94a3b8', marginBottom: 6 }
const statValue = { fontSize: 28, fontWeight: 700, color: 'white' }

export default function DashboardPage() {
  const [dashboard, setDashboard] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    loadDashboard()
  }, [])

  async function loadDashboard() {
    setLoading(true)
    try {
      const data = await api.dashboard()
      setDashboard(data)
    } catch (err) {
      setError(String(err))
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div style={{ padding: 24, color: '#94a3b8' }}>Loading dashboard…</div>
  }

  if (error) {
    return <div style={{ padding: 24, color: '#fca5a5' }}>Error: {error}</div>
  }

  if (!dashboard) {
    return <div style={{ padding: 24, color: '#94a3b8' }}>No data</div>
  }

  const { stats, recent_imports, review_queues } = dashboard

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ marginTop: 0, marginBottom: 24 }}>Dashboard</h1>

      {/* Quick Stats */}
      <div style={box}>
        <h2 style={{ marginTop: 0, marginBottom: 20 }}>Quick Stats</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div style={stat}>
            <div style={statLabel}>Total Questions</div>
            <div style={statValue}>{stats.total_questions}</div>
          </div>
          <div style={stat}>
            <div style={statLabel}>Approved Questions</div>
            <div style={{ ...statValue, color: '#86efac' }}>{stats.approved_questions}</div>
          </div>
          <div style={stat}>
            <div style={statLabel}>Needs Review</div>
            <div style={{ ...statValue, color: '#fcd34d' }}>{stats.needs_review_questions}</div>
          </div>
          <div style={stat}>
            <div style={statLabel}>Total Quizzes</div>
            <div style={statValue}>{stats.total_quizzes}</div>
          </div>
          <div style={stat}>
            <div style={statLabel}>Total Imports</div>
            <div style={statValue}>{stats.total_imports}</div>
          </div>
          <div style={stat}>
            <div style={statLabel}>Pending Reviews</div>
            <div style={{ ...statValue, color: '#fca5a5' }}>{stats.pending_import_reviews}</div>
          </div>
        </div>
      </div>

      {/* Review Queues */}
      {review_queues && review_queues.some(q => q.count > 0) && (
        <div style={box}>
          <h2 style={{ marginTop: 0, marginBottom: 20 }}>Review Queues</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {review_queues.map(queue => {
              const bucketTone = {
                ready: { bg: '#052e16', fg: '#86efac' },
                needs_review: { bg: '#3f2c06', fg: '#fcd34d' },
                needs_conversion: { bg: '#3b0764', fg: '#d8b4fe' },
                duplicate: { bg: '#172554', fg: '#93c5fd' },
                broken: { bg: '#450a0a', fg: '#fca5a5' },
              }[queue.bucket] || { bg: '#1e293b', fg: '#94a3b8' }

              return (
                <button
                  key={queue.bucket}
                  onClick={() => navigate(`/imports?bucket=${queue.bucket}`)}
                  style={{
                    ...stat,
                    background: bucketTone.bg,
                    border: 'none',
                    cursor: 'pointer',
                    padding: 20,
                  }}
                >
                  <div style={{ ...statLabel, color: bucketTone.fg, marginBottom: 10 }}>
                    {queue.bucket.replace('_', ' ')}
                  </div>
                  <div style={{ ...statValue, color: bucketTone.fg }}>
                    {queue.count}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent Imports */}
      {recent_imports && recent_imports.length > 0 && (
        <div style={box}>
          <h2 style={{ marginTop: 0, marginBottom: 20 }}>Recent Imports</h2>
          <div>
            {recent_imports.map((imp, idx) => (
              <div key={imp.id} style={{
                padding: 12,
                borderBottom: idx < recent_imports.length - 1 ? '1px solid #1e293b' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{imp.file_name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {imp.status} · {imp.created_at ? new Date(imp.created_at).toLocaleDateString() : 'Unknown date'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {imp.pending_review_count > 0 && (
                    <div style={{ background: '#fcd34d', color: '#0b1120', padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                      {imp.pending_review_count} pending
                    </div>
                  )}
                  <button
                    onClick={() => window.location.href = `/imports?run=${imp.id}`}
                    style={{...btn, padding: '8px 12px', fontSize: 12}}
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={box}>
        <h2 style={{ marginTop: 0, marginBottom: 20 }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/imports')} style={btn}>
            Go to Imports
          </button>
          <button onClick={() => navigate('/bank')} style={btn}>
            Question Bank
          </button>
          <button onClick={() => navigate('/quizzes')} style={btn}>
            My Quizzes
          </button>
        </div>
      </div>
    </div>
  )
}
